"use client"

import { useEffect, useMemo, useRef, useState, Suspense } from "react"
import { usePrerenderSafeSearchParams } from "@/hooks/use-prerender-safe-search-params"
import { SearchParamsHydrationGate } from "@/components/ssr/search-params-hydration-gate"
import Image from "next/image"
import Link from "next/link"
import { amazonUrl } from "@/lib/affiliate"
import {
  Cpu,
  DollarSign,
  GitFork,
  HardHat,
  Info,
  Package,
  Shovel,
  ShieldCheck,
  Sprout,
  Wrench,
  type LucideIcon,
} from "lucide-react"
import { HeadlineHero, RelatedTools, ResultEmailCaptureCard, ToolPageLoadingFallback, ToolPageShell, ToolWideRegion } from "@/components/tools/shared"
import { UnifiedShareButton } from "@/components/tools/shared/unified-share-button"
import { NewsletterSignupModal } from "@/components/ui/newsletter-signup-modal"
import { useNewsletterPrompt } from "@/hooks/useNewsletterPrompt"
import { useFromTool } from "@/hooks/use-from-tool"
import { analytics, contentAnalytics, extractAsin, runWhenPageVisible, stashEmailCaptureAttribution } from "@/lib/analytics"
import { useAffiliateImpression } from "@/hooks/use-affiliate-impression"
import { cn } from "@/lib/utils"
import { AffiliateDisclosure } from "@/components/tools/shared/affiliate-disclosure"
import { CollapsibleInline } from "@/components/tools/shared/collapsible-section"
import { ToolCrossLink } from "@/components/tools/shared/tool-cross-link"
import {
  calculateSprinklerCost,
  SPRINKLER_REGIONS,
  SPRINKLER_SIZE_PRESETS,
  ZONE_COVERAGE_OPTIONS,
  type InstallType,
  type SprinklerRegion,
} from "@/lib/sprinkler-cost-calculator"

const BREAKDOWN_ICONS: Record<string, LucideIcon> = {
  labor: Shovel,
  pipe: Shovel,
  heads: Sprout,
  valves: GitFork,
  controller: Cpu,
  backflow: ShieldCheck,
}

const PICKS: Record<InstallType, Array<{ name: string; url: string; image: string }>> = {
  pro: [
    {
      name: "Rachio 3 Smart Sprinkler Controller 8-Zone",
      url: amazonUrl("B07CZ864Y9", "tool"),
      image: "https://m.media-amazon.com/images/I/71asdFrVrlL._SL500_.jpg",
    },
    {
      name: "Orbit B-hyve Smart Hose Watering Timer",
      url: amazonUrl("B0758NR8DJ", "tool"),
      image: "https://m.media-amazon.com/images/I/41ANo8nPdcL._AC_SL200_.jpg",
    },
  ],
  diy: [
    {
      name: "Orbit B-hyve Smart Hose Watering Timer",
      url: amazonUrl("B0758NR8DJ", "tool"),
      image: "https://m.media-amazon.com/images/I/41ANo8nPdcL._AC_SL200_.jpg",
    },
    {
      name: "Rachio 3 Smart Sprinkler Controller 8-Zone",
      url: amazonUrl("B07CZ864Y9", "tool"),
      image: "https://m.media-amazon.com/images/I/71asdFrVrlL._SL500_.jpg",
    },
  ],
}

const PICKS_SURFACE = "tool-sprinkler-cost-picks"

// Impression + click on the same surface string so PostHog can join
// affiliate_product_shown / affiliate_product_clicked and compute CTR. Extracted
// into a row component only because the impression hook must run per card.
function PickRow({
  pick,
  pickIndex,
  install,
}: {
  pick: { name: string; url: string; image: string }
  pickIndex: number
  install: InstallType
}) {
  const impressionRef = useAffiliateImpression(PICKS_SURFACE, {
    article_slug: "tool:sprinkler-cost-calculator",
    product_name: pick.name,
    affiliate_link: pick.url,
    asin: extractAsin(pick.url) ?? undefined,
    source_context: install,
    pick_index: pickIndex,
    position: pickIndex + 1,
  })

  return (
    <div
      ref={impressionRef}
      className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-3.5 py-3"
    >
      <div className="grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-lg border border-gray-100 bg-white">
        <Image
          src={pick.image}
          alt={pick.name}
          width={56}
          height={56}
          className="h-full w-full object-contain p-1"
        />
      </div>
      <div className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-gray-900">{pick.name}</span>
        <a
          href={pick.url}
          target="_blank"
          rel="noopener noreferrer nofollow sponsored"
          data-affiliate="true"
          data-affiliate-placement={PICKS_SURFACE}
          onClick={() =>
            contentAnalytics.affiliateProductClicked(PICKS_SURFACE, {
              productName: pick.name,
              affiliateLink: pick.url,
              article_slug: "tool:sprinkler-cost-calculator",
              source_context: install,
              pick_index: pickIndex,
              position: pickIndex + 1,
            })
          }
          className="inline-flex min-h-11 items-center text-[12.5px] font-semibold text-green-600 hover:text-green-700"
        >
          Check price on Amazon →
        </a>
      </div>
    </div>
  )
}

function parseArea(value: string | null): number {
  const n = Number(value)
  return Number.isFinite(n) && n > 0 && n <= 1_000_000 ? Math.round(n) : 8000
}

function parseInstall(value: string | null): InstallType {
  return value === "diy" ? "diy" : "pro"
}

function parseRegion(value: string | null): SprinklerRegion {
  return value === "north" || value === "south" ? value : "transition"
}

const ZONE_COVERAGE_VALUES = ZONE_COVERAGE_OPTIONS.map((o) => o.sqft)

function parseZoneCoverage(value: string | null): number {
  const n = Number(value)
  return (ZONE_COVERAGE_VALUES as readonly number[]).includes(n) ? n : 1500
}

const SELECTED_TILE = "border-2 border-green-600 bg-green-50"
const DEFAULT_TILE = "border border-gray-200 bg-white hover:border-green-300"

function SprinklerCostCalculatorContent() {
  const searchParams = usePrerenderSafeSearchParams()
  useFromTool("sprinkler-cost-calculator")
  const { trackUsage, showNewsletterModal, closeNewsletterModal, usageCount } = useNewsletterPrompt()

  const [area, setArea] = useState(() => parseArea(searchParams.get("area")))
  const [install, setInstall] = useState<InstallType>(() => parseInstall(searchParams.get("install")))
  const [region, setRegion] = useState<SprinklerRegion>(() => parseRegion(searchParams.get("region")))
  const [sqftPerZone, setSqftPerZone] = useState(() => parseZoneCoverage(searchParams.get("zone")))
  const trackedUsageRef = useRef(false)

  const result = useMemo(
    () => calculateSprinklerCost({ areaSqFt: area, install, region, sqftPerZone }),
    [area, install, region, sqftPerZone],
  )

  // Live-update tool: analytics fire once per session on the first deliberate
  // input change. setState hasn't re-rendered yet when this runs, so callers
  // pass the values they just set and the payload is computed from those.
  const trackOnce = (
    next?: Partial<{ area: number; install: InstallType; region: SprinklerRegion; sqftPerZone: number }>,
  ) => {
    if (trackedUsageRef.current) return
    trackedUsageRef.current = true
    trackUsage("sprinkler-cost-calculator")
    const inputs = { area, install, region, sqftPerZone, ...next }
    const nextResult = calculateSprinklerCost({ ...inputs, areaSqFt: inputs.area })
    analytics.calculatorUsed("sprinkler-cost", {
      inputs: { area: inputs.area, install: inputs.install, region: inputs.region, restored: false },
      result: { typical: nextResult.typicalLabel, zones: nextResult.zones },
      source: "live_update",
    })
  }

  // Share-link restore. shareUrl carries the full input set, so a recipient sees
  // a finished result they never calculated and trackOnce (selection-change only)
  // never fires — the same undercount the lawn-size share path had. `restored`
  // separates it from a fresh run, matching grass_id_result_viewed. Fires once,
  // only when a share param is actually present, and only once the tab is
  // visible: an unconditional mount fire is the 2026-08-01 double-count bug, and
  // a hidden mount is a phantom session.
  const restoreTrackedRef = useRef(false)
  useEffect(() => {
    const hasShareParams = ["area", "install", "region", "zone"].some((key) =>
      Boolean(searchParams.get(key)),
    )
    if (!hasShareParams || restoreTrackedRef.current) return
    return runWhenPageVisible(() => {
      if (restoreTrackedRef.current) return
      restoreTrackedRef.current = true
      analytics.calculatorUsed("sprinkler-cost", {
        inputs: { area, install, region, restored: true },
        result: { typical: result.typicalLabel, zones: result.zones },
        source: "url_params",
      })
    })
    // Restore state is read once from the URL the page mounted with; later
    // input changes are the interaction path's job, not this one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  const updateArea = (v: number) => {
    setArea(v)
    trackOnce({ area: v })
  }
  const updateInstall = (v: InstallType) => {
    setInstall(v)
    trackOnce({ install: v })
  }
  const updateRegion = (v: SprinklerRegion) => {
    setRegion(v)
    trackOnce({ region: v })
  }

  const shareUrl = `/tools/sprinkler-cost-calculator?area=${area}&install=${install}&region=${region}&zone=${sqftPerZone}`
  const picks = PICKS[install]

  return (
    <ToolPageShell
      breadcrumbLabel="Sprinkler Cost Calculator"
      after={
        <NewsletterSignupModal
          isOpen={showNewsletterModal}
          onClose={closeNewsletterModal}
          toolName="Sprinkler Cost Calculator"
          usageCount={usageCount}
        />
      }
    >
      <HeadlineHero
        eyebrow="2026 national averages"
        eyebrowIcon={<DollarSign className="h-3.5 w-3.5" aria-hidden="true" />}
        title="What does a sprinkler system"
        accent="really cost?"
        subtitle="Enter your lawn size, pick pro or DIY, and get a realistic range with the full breakdown: heads, valves, controller, and labor."
      />

      <ToolWideRegion>
        <div className="grid items-start gap-6 lg:grid-cols-2">
          {/* Inputs */}
          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-elevation-2 sm:p-7">
            <p className="mb-3 text-[13px] font-bold uppercase tracking-wider text-green-700">
              1 · Lawn size
            </p>
            <div className="relative">
              <input
                type="number"
                min={0}
                inputMode="numeric"
                value={area}
                aria-label="Lawn size in square feet"
                onChange={(e) => {
                  const v = parseFloat(e.target.value)
                  updateArea(Number.isNaN(v) ? 0 : Math.max(0, Math.round(v)))
                }}
                className="w-full min-h-13 rounded-xl border border-gray-200 bg-white pl-4 pr-[74px] text-[17px] font-bold text-gray-900 outline-none transition-colors focus:border-green-600 focus:ring-4 focus:ring-green-200/60"
              />
              <span className="pointer-events-none absolute top-1/2 right-4 -translate-y-1/2 text-[13px] font-semibold text-gray-500">
                sq ft
              </span>
            </div>
            <div className="mt-2.5 flex flex-wrap gap-2" role="radiogroup" aria-label="Common lawn sizes">
              {SPRINKLER_SIZE_PRESETS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  role="radio"
                  aria-checked={preset === area}
                  onClick={() => updateArea(preset)}
                  className={cn(
                    "inline-flex min-h-11 items-center rounded-full px-3.5 text-[13px] font-semibold text-gray-900 transition-colors",
                    preset === area ? SELECTED_TILE : DEFAULT_TILE,
                  )}
                >
                  {preset === 21780 ? "1/2 acre" : preset.toLocaleString("en-US")}
                </button>
              ))}
            </div>
            <p className="mt-2.5 text-[13px] text-gray-500">
              Not sure?{" "}
              <Link
                href="/tools/lawn-size-calculator"
                className="font-semibold text-green-600 underline underline-offset-2 hover:text-green-700"
              >
                Measure with the lawn size calculator
              </Link>
            </p>

            <p className="mt-6 mb-3 text-[13px] font-bold uppercase tracking-wider text-green-700">
              2 · Who&apos;s installing?
            </p>
            <div className="flex gap-2.5" role="radiogroup" aria-label="Installation type">
              <button
                type="button"
                role="radio"
                aria-checked={install === "pro"}
                onClick={() => updateInstall("pro")}
                className={cn(
                  "flex min-h-[70px] flex-1 flex-col items-center gap-1 rounded-2xl p-3 transition-colors",
                  install === "pro" ? SELECTED_TILE : DEFAULT_TILE,
                )}
              >
                <HardHat className="h-5 w-5 text-green-600" aria-hidden="true" />
                <span className="text-sm font-bold text-gray-900">Professional</span>
                <span className="text-[11.5px] text-gray-500">turnkey, warrantied</span>
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={install === "diy"}
                onClick={() => updateInstall("diy")}
                className={cn(
                  "flex min-h-[70px] flex-1 flex-col items-center gap-1 rounded-2xl p-3 transition-colors",
                  install === "diy" ? SELECTED_TILE : DEFAULT_TILE,
                )}
              >
                <Wrench className="h-5 w-5 text-green-600" aria-hidden="true" />
                <span className="text-sm font-bold text-gray-900">DIY</span>
                <span className="text-[11.5px] text-gray-500">materials + your weekend</span>
              </button>
            </div>

            <p className="mt-6 mb-3 text-[13px] font-bold uppercase tracking-wider text-green-700">
              3 · Your region
            </p>
            <div className="flex gap-2" role="radiogroup" aria-label="Climate region">
              {(Object.keys(SPRINKLER_REGIONS) as SprinklerRegion[]).map((key) => (
                <button
                  key={key}
                  type="button"
                  role="radio"
                  aria-checked={key === region}
                  onClick={() => updateRegion(key)}
                  className={cn(
                    "flex min-h-14 flex-1 flex-col items-center justify-center gap-0.5 rounded-2xl px-1.5 py-2.5 transition-colors",
                    key === region ? SELECTED_TILE : DEFAULT_TILE,
                  )}
                >
                  <span className="text-[13.5px] font-bold text-gray-900">
                    {SPRINKLER_REGIONS[key].label}
                  </span>
                  <span className="text-[11px] text-gray-500">{SPRINKLER_REGIONS[key].sub}</span>
                </button>
              ))}
            </div>
            <p className="mt-3 text-[13px] leading-normal text-gray-500">{result.regionNote}</p>

            <CollapsibleInline
              title="Advanced: zone coverage"
              storageKey="sprinkler-cost-advanced"
              className="mt-5"
            >
              <div className="flex gap-2" role="radiogroup" aria-label="Zone coverage">
                {ZONE_COVERAGE_OPTIONS.map((option) => (
                  <button
                    key={option.sqft}
                    type="button"
                    role="radio"
                    aria-checked={option.sqft === sqftPerZone}
                    onClick={() => {
                      setSqftPerZone(option.sqft)
                      trackOnce({ sqftPerZone: option.sqft })
                    }}
                    className={cn(
                      "flex min-h-14 flex-1 flex-col items-center justify-center gap-0.5 rounded-2xl px-1.5 py-2.5 transition-colors",
                      option.sqft === sqftPerZone ? SELECTED_TILE : DEFAULT_TILE,
                    )}
                  >
                    <span className="text-[13px] font-bold text-gray-900">{option.label}</span>
                    <span className="text-[11px] text-gray-500">{option.sub}</span>
                  </button>
                ))}
              </div>
              <p className="mt-2.5 text-xs leading-relaxed text-gray-500">
                Spray heads cover less area per zone than rotors at the same water supply, so
                small or cut-up yards need more zones per square foot. Assuming{" "}
                {sqftPerZone.toLocaleString("en-US")} sq ft per zone.
              </p>
            </CollapsibleInline>
          </div>

          {/* Result: the peak frame, same surface as ResultSuccessCard */}
          <div data-tool-result="" className="tool-peak-card overflow-hidden">
            <div className="p-5 sm:p-6" aria-live="polite">
              <div className="flex items-start justify-between gap-3">
                <div data-result-heading="" className="min-w-0">
                  <span
                    key={result.typicalLabel}
                    className="tool-value-swap block text-5xl font-extrabold leading-none tracking-tight tabular-nums text-green-700"
                  >
                    {result.typicalLabel}
                  </span>
                  <p className="mt-1.5 text-sm font-medium text-gray-500">
                    {install === "pro" ? "typical installed cost" : "typical DIY materials cost"}
                  </p>
                </div>
                <UnifiedShareButton
                  url={shareUrl}
                  title="My sprinkler system cost estimate"
                  text={`Sprinkler system for ${result.areaLabel} sq ft (${install === "pro" ? "pro install" : "DIY"}): typically ${result.typicalLabel}, range ${result.lowLabel} – ${result.highLabel}`}
                  size="sm"
                />
              </div>
              <p className="mt-4 text-pretty text-base leading-relaxed text-gray-700">
                {install === "pro" ? "A pro install" : "A DIY build"} for your {result.areaLabel} sq ft lawn in
                the {SPRINKLER_REGIONS[region].label.toLowerCase()} region runs {result.lowLabel} to {result.highLabel},
                about {result.perSqFtLabel} per sq ft.
              </p>

              <div className="mt-5 grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-green-200/70 bg-white/70 px-3 py-2.5">
                  <div className="flex items-center gap-1.5 text-gray-500">
                    <GitFork className="h-4 w-4 text-green-600/80" aria-hidden="true" />
                    <span className="text-xs font-semibold uppercase tracking-wide">Zones</span>
                  </div>
                  <p className="mt-1 text-lg font-bold tabular-nums text-green-700">{result.zones}</p>
                </div>
                <div className="rounded-xl border border-green-200/70 bg-white/70 px-3 py-2.5">
                  <div className="flex items-center gap-1.5 text-gray-500">
                    <DollarSign className="h-4 w-4 text-green-600/80" aria-hidden="true" />
                    <span className="text-xs font-semibold uppercase tracking-wide">Per zone</span>
                  </div>
                  <p className="mt-1 text-lg font-bold tabular-nums text-green-700">{result.perZoneLabel}</p>
                </div>
              </div>

              <h3 className="mt-6 mb-2.5 text-base font-semibold text-gray-900">
                Where the money goes
              </h3>
              <div className="flex flex-col gap-2">
                {result.breakdown.map((line) => {
                  const IconComponent = BREAKDOWN_ICONS[line.id] ?? Package
                  return (
                    <div
                      key={line.id}
                      className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-3.5 py-3"
                    >
                      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-green-50 text-green-600">
                        <IconComponent className="h-[18px] w-[18px]" aria-hidden="true" />
                      </span>
                      <span className="flex-1 text-sm font-semibold text-gray-900">{line.label}</span>
                      <span className="text-sm font-extrabold text-gray-900">{line.costLabel}</span>
                    </div>
                  )
                })}
              </div>

              <div className="mt-5 flex items-start gap-2.5 rounded-xl border border-green-200 bg-green-50 px-4 py-3.5">
                <Info className="mt-0.5 h-[18px] w-[18px] shrink-0 text-green-600" aria-hidden="true" />
                <p className="text-[13.5px] leading-relaxed text-green-900">{result.tip}</p>
              </div>

              <h3 className="mt-6 mb-2.5 text-base font-semibold text-gray-900">
                {install === "pro" ? "While you wait for quotes" : "DIY starter picks"}
              </h3>
              <div className="flex flex-col gap-2">
                {picks.map((pick, i) => (
                  <PickRow key={pick.name} pick={pick} pickIndex={i} install={install} />
                ))}
              </div>
              <AffiliateDisclosure className="mt-2.5" />
            </div>
          </div>
        </div>
      </ToolWideRegion>

      <div className="mt-8">
        <ResultEmailCaptureCard
          source="tool_result_sprinkler_cost"
          toolDisplayName="sprinkler cost estimate"
          subcopy="Keep this sprinkler cost estimate handy and track your lawn over time."
          accountLead={{
            redirectTo: shareUrl,
            headline: "Save my sprinkler cost estimate",
            subcopy: "Keep this sprinkler cost estimate in a free account and track your lawn over time.",
            onBeforeOAuth: () => stashEmailCaptureAttribution("tool_result_sprinkler_cost"),
          }}
          successSubcopy="Your lawn tips are on the way. Your sprinkler cost estimate stays right here to revisit anytime."
          onCaptured={() => closeNewsletterModal()}
        />
      </div>

      {/* Watering schedule cross-link */}
      <ToolCrossLink
        className="mt-8"
        href="/tools/watering-schedule"
        lead="Already have sprinklers?"
        body="Set inches per week and run times with the Watering Schedule tool."
        cta="Build my watering schedule"
      />

      <RelatedTools
        currentToolId="sprinkler-cost-calculator"
        prefillValues={{}}
        className="mt-10"
      />
    </ToolPageShell>
  )
}

export function SprinklerCostCalculatorPageContent() {
  return (
    <Suspense fallback={<ToolPageLoadingFallback toolId="sprinkler-cost-calculator" />}>
      <SearchParamsHydrationGate>
        <SprinklerCostCalculatorContent />
      </SearchParamsHydrationGate>
    </Suspense>
  )
}
